import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { platformApi } from '../../api/platformApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Plus, Edit2, Trash2, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

export const PlatformPlans = () => {
    const { token } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        features_json: '{}',
        limits_json: '{}'
    });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const data = await platformApi.getPlans(token);
            setPlans(data);
        } catch (error) {
            toast.error("Error cargando planes");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setIsEditing(false);
        setFormData({
            code: '',
            name: '',
            features_json: JSON.stringify({
                trademarks: true,
                clients: true,
                procedures: true,
                reports: true,
                alerts: true,
                gazette: true,
                users: true
            }, null, 2),
            limits_json: '{\n  "max_users": 5\n}'
        });
        setModalOpen(true);
    };

    const handleOpenEdit = (plan) => {
        setIsEditing(true);
        setFormData({
            id: plan.id,
            code: plan.code,
            name: plan.name,
            features_json: JSON.stringify(plan.features || {}, null, 2),
            limits_json: JSON.stringify(plan.limits || {}, null, 2)
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            // Validate JSON
            let features, limits;
            try {
                features = JSON.parse(formData.features_json);
                limits = JSON.parse(formData.limits_json);
            } catch (e) {
                toast.error("JSON inválido en features o limits");
                return;
            }

            const payload = {
                code: formData.code,
                name: formData.name,
                features,
                limits,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            if (isEditing) {
                await platformApi.updatePlan(token, formData.id, payload);
                toast.success("Plan actualizado");
            } else {
                payload.id = crypto.randomUUID();
                await platformApi.createPlan(token, payload);
                toast.success("Plan creado");
            }

            setModalOpen(false);
            fetchPlans();
        } catch (error) {
            toast.error("Error guardando plan");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Seguro que deseas eliminar este plan?")) return;
        try {
            await platformApi.deletePlan(token, id);
            toast.success("Plan eliminado");
            fetchPlans();
        } catch (error) {
            toast.error("Error eliminando plan");
        }
    };

    if (loading) return <Loader2 className="animate-spin" />;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Planes de Suscripción</h3>
                <Button onClick={handleOpenCreate}><Plus className="mr-2 h-4 w-4" /> Nuevo Plan</Button>
            </div>

            <div className="border rounded-md bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Código</TableHead>
                            <TableHead>Nombre</TableHead>
                            <TableHead>Límites</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {plans.map(plan => (
                            <TableRow key={plan.id}>
                                <TableCell className="font-mono">{plan.code}</TableCell>
                                <TableCell className="font-medium">{plan.name}</TableCell>
                                <TableCell className="text-xs text-muted-foreground whitespace-pre-wrap">
                                    {JSON.stringify(plan.limits)}
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                    <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(plan)}>
                                        <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(plan.id)}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{isEditing ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Código</Label>
                                <Input disabled={isEditing} value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })} placeholder="FREE" />
                            </div>
                            <div>
                                <Label>Nombre</Label>
                                <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Plan Gratuito" />
                            </div>
                        </div>
                        <div>
                            <Label>Features (JSON)</Label>
                            <textarea
                                className="w-full h-32 p-2 border rounded-md text-xs font-mono"
                                value={formData.features_json}
                                onChange={e => setFormData({ ...formData, features_json: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Límites (JSON)</Label>
                            <textarea
                                className="w-full h-32 p-2 border rounded-md text-xs font-mono"
                                value={formData.limits_json}
                                onChange={e => setFormData({ ...formData, limits_json: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                        <Button onClick={handleSave}><Save className="mr-2 h-4 w-4" /> Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
